import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  deleteStreamSetting,
  getAllCourtRoomsForController,
  getAllStreamSettings,
} from "../../../../api/retensionApis";
import type { CourtType } from "../../../../components/CourtToggle";
import { useAuth } from "../../../../context/AuthProvider";
import logger from "../../../../utils/logger";
import {
  DEFAULT_STREAM_FORM,
  STREAM_PAGE_SIZE,
} from "./StreamSetting.constants";
import type {
  LinkType,
  StreamLink,
  StreamSettingPayload,
  StreamSettingRow,
  StreamSettingSelectionLabels,
} from "./StreamSetting.types";
import DeleteStreamSettingModal from "./DeleteStreamSettingModal";
import SeeMoreLinksModal from "./SeeMoreLinksModal";
import StreamSettingsModal from "./StreamSettingsModal";
import StreamSettingsTable from "./StreamSettingsTable";

interface ApiResponse<T = unknown> {
  data?: {
    status?: boolean;
    data?: T;
    streamSettings?: StreamSettingRow[];
  };
}

const roleIsSuperAdmin = (role?: string) =>
  role === "SuperAdmin" || role === "Superadmin";

const getStreamLabel = (link: StreamLink, index: number): LinkType["label"] => {
  const type = link.type || link.linkFor;

  if (type === "facebook" || index === 0) return "Facebook";
  if (type === "youtube" || index === 1) return "Youtube";

  return "Custom Link";
};

const getEntityId = (value?: string | { _id?: string }) => {
  if (!value) return "";
  if (typeof value === "string") return value;

  return value._id || "";
};

const highCourtLabel = (value?: StreamSettingRow["highcourtId"]) =>
  typeof value === "object" ? value.highCourtName || "" : "";

const benchLabel = (value?: StreamSettingRow["benchId"]) =>
  typeof value === "object" ? value.benchName || "" : "";

// const benchRoomLabel = (value?: StreamSettingRow["benchRoomId"]) => {
//   if (typeof value !== "object") return "";

//   return [value.courtRoom, value.benchName].filter(Boolean).join(" - ");
// };
const benchRoomLabel = (value?: StreamSettingRow["benchRoomId"]) => {
  if (!value || typeof value !== "object") return "Court Deleted";

  const label = [value.courtRoom, value.benchName].filter(Boolean).join(" - ");

  return label || "Court Deleted";
};

const StreamSetting: React.FC = () => {
  const { user } = useAuth();

  const [linkType, setLinkType] = useState<LinkType[]>([]);
  const [allStreamData, setAllStreamData] = useState<StreamSettingRow[]>([]);
  const [seeMoreLinks, setSeeMoreLinks] = useState<StreamLink[]>([]);

  const [page, setPage] = useState(1);
  const [courtType, setCourtType] = useState<CourtType>("high");

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [seeMoreModal, setSeeMoreModal] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [formValues, setFormValues] =
    useState<StreamSettingPayload>(DEFAULT_STREAM_FORM);
  const [selectionLabels, setSelectionLabels] =
    useState<StreamSettingSelectionLabels>({});
  const [isDeleting, setIsDeleting] = useState(false);

  const totalPages = Math.ceil(allStreamData.length / STREAM_PAGE_SIZE);

  const paginatedData = useMemo(
    () =>
      allStreamData.slice(
        (page - 1) * STREAM_PAGE_SIZE,
        page * STREAM_PAGE_SIZE,
      ),
    [allStreamData, page],
  );

  useEffect(() => {
    if (page > 1 && paginatedData.length === 0) {
      setPage((prev) => Math.max(prev - 1, 1));
    }
  }, [paginatedData, page]);

  const resetStreamForm = () => {
    setEditId(null);
    setFormValues(DEFAULT_STREAM_FORM);
    setSelectionLabels({});
    setLinkType([]);
  };

  const closeStreamForm = () => {
    setShowFormModal(false);
    resetStreamForm();
  };

  const loadStreamSettings = async () => {
    try {
      if (roleIsSuperAdmin(user?.role)) {
        const res = (await getAllStreamSettings()) as ApiResponse<
          StreamSettingRow[]
        >;

        if (res?.data?.status) {
          setAllStreamData(res.data.data || []);
        }

        return;
      }

      if (user?.role === "Controller") {
        const res = (await getAllCourtRoomsForController()) as ApiResponse;

        if (res?.data?.status) {
          setAllStreamData(res.data.streamSettings || []);
        }
      }
    } catch (err) {
      logger.error("Failed to load stream settings", err);
    }
  };

  useEffect(() => {
    if (user?.role) {
      loadStreamSettings();
    }
  }, [user?.role]);

  const handleStreamSaved = async () => {
    await loadStreamSettings();
    setShowFormModal(false);
    resetStreamForm();
  };

  const handleEdit = (item: StreamSettingRow) => {
    const clonedLinks = (item.links || []).map((link) => ({
      type: link.type || link.linkFor || "custom",
      link: link.link,
      key: link.key,
    }));

    setEditId(item._id);
    setCourtType(item.courtRoomModel === "CourtRoom" ? "district" : "high");
    setLinkType(
      clonedLinks.map((link, index) => ({
        index,
        label: getStreamLabel(link, index),
      })),
    );
    setFormValues({
      highcourtId:
        getEntityId(item.highcourtId) || getEntityId(item.courtRoom?.highcourt),
      benchId: getEntityId(item.benchId) || getEntityId(item.courtRoom?.bench),
      benchRoomId:
        getEntityId(item.benchRoomId) ||
        (item.courtRoomModel === "BenchRooms"
          ? getEntityId(item.courtRoom?._id)
          : ""),
      stateId: getEntityId(item.stateId),
      districtId: getEntityId(item.districtId),
      complexId: getEntityId(item.complexId),
      establishmentId: getEntityId(item.establishmentId),
      courtRoomId: item.courtRoom?._id || "",
      delayInMinutes: item.delayInMinutes,
      links: clonedLinks,
    });
    setSelectionLabels({
      highcourt: highCourtLabel(item.highcourtId),
      bench: benchLabel(item.benchId),
      benchRoom:
        benchRoomLabel(item.benchRoomId) ||
        (item.courtRoomModel === "BenchRooms"
          ? [item.courtRoom?.number, item.courtRoom?.name]
              .filter(Boolean)
              .join(" - ")
          : ""),
      state: typeof item.stateId === "string" ? item.stateId : "",
      courtRoom:
        item.courtRoomModel === "CourtRoom"
          ? [item.courtRoom?.number, item.courtRoom?.name]
              .filter(Boolean)
              .join(" - ")
          : "",
    });
    setShowFormModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);
      await deleteStreamSetting(deleteId);
      toast.success("Deleted successfully");
      await loadStreamSettings();
      setShowDeleteModal(false);
      setDeleteId(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete stream setting",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const removeLinkBlock = (index: number) => {
    setFormValues((prev) => ({
      ...prev,
      links: prev.links.filter((_, currentIndex) => currentIndex !== index),
    }));

    setLinkType((prev) =>
      prev
        .filter((_, currentIndex) => currentIndex !== index)
        .map((type, currentIndex) => ({
          ...type,
          index: currentIndex,
        })),
    );
  };

  const openAddStreamModal = () => {
    resetStreamForm();
    setShowFormModal(true);
  };

  return (
    <>
      {showFormModal && (
        <StreamSettingsModal
          courtType={courtType}
          editId={editId}
          formValues={formValues}
          linkType={linkType}
          onClose={closeStreamForm}
          onCourtTypeChange={setCourtType}
          onFormValuesChange={setFormValues}
          onLinkTypeChange={setLinkType}
          onRemoveLink={removeLinkBlock}
          onSaved={handleStreamSaved}
          selectedLabels={selectionLabels}
        />
      )}

      {showDeleteModal && (
        <DeleteStreamSettingModal
          isDeleting={isDeleting}
          onCancel={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
        />
      )}

      {seeMoreModal && (
        <SeeMoreLinksModal
          links={seeMoreLinks}
          onClose={() => setSeeMoreModal(false)}
        />
      )}

      <StreamSettingsTable
        currentPage={page}
        pageSize={STREAM_PAGE_SIZE}
        paginatedData={paginatedData}
        totalPages={totalPages}
        onAdd={openAddStreamModal}
        onDeleteClick={(id) => {
          setDeleteId(id);
          setShowDeleteModal(true);
        }}
        onEdit={handleEdit}
        onPageChange={setPage}
        onSeeMore={(links) => {
          setSeeMoreLinks(links);
          setSeeMoreModal(true);
        }}
      />
    </>
  );
};

export default StreamSetting;
